import { t } from "@lingui/macro";
import {
  ArrowDownIcon,
  ArrowRightIcon,
  CubeFocusIcon,
  MinusIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";

import { useBuilderStore } from "@/client/stores/builder";

/*
 * Les mêmes niveaux sont utilisés
 * par la toolbar native du builder.
 */
const ZOOM_LEVELS = [0.38, 0.45, 0.5, 0.65, 0.8, 1, 1.25];

export const PreviewToolbar = () => {
  const frame = useBuilderStore((state) => state.frame.ref);

  const [scale, setScale] = useState(ZOOM_LEVELS[1]);
  const [totalPages, setTotalPages] = useState(1);

  const [autoFit, setAutoFit] = useState(true);

  const send = useCallback(
    (message: Record<string, unknown>) => {
      frame?.contentWindow?.postMessage(message, "*");
    },
    [frame],
  );

  const fitToScreen = useCallback(() => {
    setAutoFit(true);

    send({
      type: "RESET_VIEW",
    });
  }, [send]);

  const changeZoom = (nextIndex: number) => {
    const index = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, nextIndex));

    setAutoFit(false);
    setScale(ZOOM_LEVELS[index]);

    /*
     * L’artboard attend `scale`.
     * Il ne faut pas utiliser `payload`.
     */
    send({
      type: "SET_ZOOM",
      scale: ZOOM_LEVELS[index],
    });
  };

  useEffect(() => {
    if (!frame) {
      return;
    }

    const initialize = () => {
      /*
       * Deux frames permettent au Sheet
       * mobile de terminer son calcul de
       * dimensions avant RESET_VIEW.
       */
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          send({
            type: "RESET_VIEW",
          });
        });
      });
    };

    frame.addEventListener("load", initialize);

    initialize();

    return () => {
      frame.removeEventListener("load", initialize);
    };
  }, [frame, send]);

  useEffect(() => {
    if (!frame || !autoFit || typeof ResizeObserver === "undefined") {
      return;
    }

    let animationFrame = 0;

    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(animationFrame);

      animationFrame = window.requestAnimationFrame(() => {
        send({
          type: "RESET_VIEW",
        });
      });
    });

    observer.observe(frame);

    if (frame.parentElement) {
      observer.observe(frame.parentElement);
    }

    return () => {
      window.cancelAnimationFrame(animationFrame);

      observer.disconnect();
    };
  }, [frame, autoFit, send]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== frame?.contentWindow) {
        return;
      }

      const { type, payload } = event.data ?? {};
      if (type === "LENGTH_PAGES") setTotalPages(payload.pages);
      if (type === "ZOOM_CHANGED" && typeof payload?.scale === "number") {
        setScale(payload.scale);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [frame]);

  return (
    <div className="flex shrink-0 items-center rounded-lg bg-slate-50 p-0.5">
      <span className="sr-only">
        {t`Aperçu du CV`} · {totalPages}
      </span>

      <div className="flex items-center gap-0.5">
        <button
          type="button"
          disabled={scale <= ZOOM_LEVELS[0]}
          aria-label={t`Réduire le zoom`}
          onClick={() => {
            changeZoom(ZOOM_LEVELS.filter((level) => level < scale - 0.001).length - 1);
          }}
          className="grid size-9 place-items-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-35"
        >
          <MinusIcon size={13} />
        </button>

        <span className="w-10 text-center text-[10px] font-bold tabular-nums text-slate-600">
          {Math.round(scale * 100)}%
        </span>

        <button
          type="button"
          disabled={scale >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
          aria-label={t`Agrandir le zoom`}
          onClick={() => {
            changeZoom(ZOOM_LEVELS.findIndex((level) => level > scale + 0.001));
          }}
          className="grid size-9 place-items-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-35"
        >
          <PlusIcon size={13} />
        </button>

        <button
          type="button"
          onClick={fitToScreen}
          aria-label={t`Adapter l’aperçu à l’écran`}
          className="grid size-9 place-items-center rounded-md text-indigo-700 hover:bg-indigo-50"
        >
          <CubeFocusIcon size={14} />
        </button>
      </div>
    </div>
  );
};

export const PreviewPageLayoutToggle = () => {
  const frame = useBuilderStore((state) => state.frame.ref);
  const [totalPages, setTotalPages] = useState(1);
  const [orientation, setOrientation] = useState<"vertical" | "horizontal">("vertical");
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== frame?.contentWindow) return;
      if (event.data?.type === "LENGTH_PAGES") setTotalPages(event.data.payload.pages);
      if (event.data?.type === "PAGE_LAYOUT_CHANGED")
        setOrientation(event.data.payload.orientation);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [frame]);
  if (totalPages < 2) return null;
  const label =
    orientation === "vertical"
      ? t`Afficher les pages côte à côte`
      : t`Afficher les pages l’une sous l’autre`;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() =>
        frame?.contentWindow?.postMessage(
          {
            type: "SET_PAGE_LAYOUT",
            orientation: orientation === "vertical" ? "horizontal" : "vertical",
          },
          window.location.origin,
        )
      }
      className="grid size-10 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      {orientation === "vertical" ? (
        <ArrowRightIcon aria-hidden size={20} />
      ) : (
        <ArrowDownIcon aria-hidden size={20} />
      )}
    </button>
  );
};
